import EmptyState from 'components/EmptyState/EmptyState';
import { ErrorMessage } from 'components/Widgets/ErrorMessage';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';
import WeatherMapResizableLayout from 'features/Weather/features/WeatherMaps/components/WeatherMapResizableLayout/WeatherMapResizableLayout';
import useLocation from 'hooks/useLocation';
import useWindow from 'hooks/useWindow';
import { useCallback, useMemo, useState } from 'react';
import { getApiErrorMessage } from 'services/_infra/apiError.utils';
import { useGetEarthquakesQuery } from 'services/weather/weather.api';
import {
  WeatherMapStaleDataNotice,
  WeatherMapUpdatingStatus,
} from '../../components/WeatherMapDataStatus/WeatherMapDataStatus';
import './assets/earthquakes.scss';
import EarthquakeInformationPanel from './components/EarthquakeInformationPanel/EarthquakeInformationPanel';
import EarthquakeMapContent from './components/EarthquakeMapContent/EarthquakeMapContent';
import {
  EARTHQUAKES_PAGE_LAYOUT,
  EARTHQUAKES_QUERY_OPTIONS,
  EARTHQUAKES_REFRESH_INTERVAL_MINUTES,
  EARTHQUAKES_ROUTE,
} from './config/earthquakes.config';

const Earthquakes = () => {
  const { locations } = useLocation();
  const { width } = useWindow();
  const [selectedEarthquakeId, setSelectedEarthquakeId] = useState(null);
  const {
    data: earthquakes = [],
    error: earthquakesError,
    isError: isEarthquakesError,
    isFetching: fetchingEarthquakes,
    isLoading: loadingEarthquakes,
    refetch,
  } = useGetEarthquakesQuery(undefined, EARTHQUAKES_QUERY_OPTIONS);

  const selectedEarthquake = useMemo(
    () =>
      earthquakes.find((earthquake) => earthquake.id === selectedEarthquakeId) ??
      earthquakes[0] ??
      null,
    [earthquakes, selectedEarthquakeId],
  );

  const selectEarthquake = useCallback((earthquake) => {
    setSelectedEarthquakeId(earthquake.id);
  }, []);
  const isDesktop = width >= 992;

  if (loadingEarthquakes) {
    return <WidgetLoadingAnimation />;
  }

  if (isEarthquakesError && !earthquakes.length) {
    return <ErrorMessage message={getApiErrorMessage(earthquakesError)} onRetry={refetch} />;
  }

  if (!earthquakes.length) {
    return (
      <EmptyState
        title="Recent Earthquakes"
        message="No recent earthquake data is available right now."
      />
    );
  }

  const mapContent = (
    <EarthquakeMapContent
      earthquakes={earthquakes}
      selectedEarthquake={selectedEarthquake}
      fallbackCenter={locations.effectiveLocation?.coords}
      onSelectEarthquake={selectEarthquake}
      scrollZoom={isDesktop}
    />
  );
  const informationPanel = (
    <EarthquakeInformationPanel
      earthquakes={earthquakes}
      selectedEarthquake={selectedEarthquake}
      isRefreshing={fetchingEarthquakes && !loadingEarthquakes}
      isResizable={isDesktop}
      onSelectEarthquake={selectEarthquake}
    />
  );

  return (
    <section
      aria-label="Recent earthquakes"
      className={`earthquakes d-flex w-100 flex-column gap-2${isDesktop ? ' h-100 min-h-0' : ''}`}
    >
      <WidgetTimeUpdate
        route={EARTHQUAKES_ROUTE}
        updateIntervalBasis={EARTHQUAKES_REFRESH_INTERVAL_MINUTES}
      />

      {isEarthquakesError && (
        <WeatherMapStaleDataNotice message="The latest update failed. Displaying the most recently available earthquake data." />
      )}

      <WeatherMapResizableLayout
        {...EARTHQUAKES_PAGE_LAYOUT}
        isResizable={isDesktop}
        mapAriaLabel="Earthquake map"
        panelAriaLabel="Earthquake information"
        resizeHandleAriaLabel="Resize earthquake map and information"
        containerClassName={`earthquakes-resizable-content${isDesktop ? ' flex-grow-1' : ''}`}
        mapClassName="earthquakes-map-pane"
        panelClassName={`earthquakes-side-pane${isDesktop ? '' : ' gap-3'}`}
        mapContent={mapContent}
        panelContent={informationPanel}
      />

      {fetchingEarthquakes && !loadingEarthquakes && (
        <WeatherMapUpdatingStatus message="Updating recent earthquake data" />
      )}
    </section>
  );
};

export default Earthquakes;
